import type { ChartView } from '@/features/chart-rail/lib/panelIds'

/**
 * The chart page's tab strip, as plain data and pure moves on it (owner, 2026-10-30: "create new
 * tabs just like Chrome and toggle between the charts").
 *
 * The URL is the source of truth for what is SHOWN: `/chart/$symbol` is that symbol's chart tab,
 * `?view=screener` the pinned Advanced screener, `?view=new` the New tab. So every tab is one URL
 * and no two tabs share one: a symbol is open in one tab at most, and there is one New tab at most
 * (the `+` activates it rather than stacking a second empty page). The strip state holds only what
 * the URL cannot: which tabs exist, their order, and the symbols charted lately.
 *
 * The Advanced screener is pinned first and never closes, so it is not an item here.
 */

/** A tab in the strip after the pinned screener. `symbol` is upper-case, as the backend keys it. */
export type TabItem = { kind: 'chart'; symbol: string } | { kind: 'new' }

/** The URL's answer to "which tab is showing": the screener, the New tab, or a symbol's chart. */
export type TabKey = 'screener' | 'new' | `chart:${string}`

export interface TabsState {
  items: TabItem[]
  /** The tab last shown, so `/chart` (no symbol) reopens on it. */
  active: TabKey
  /** Symbols charted on this device, newest first: the New tab page's Recent list. */
  recent: string[]
}

/** Past this a new tab drops the leftmost one that is not showing; the `+` stops at it. */
export const MAX_TABS = 12

export const RECENT_LIMIT = 8

export const SCREENER_KEY: TabKey = 'screener'
export const NEW_KEY: TabKey = 'new'

export const chartKey = (symbol: string): TabKey => `chart:${symbol.toUpperCase()}`

export const itemKey = (item: TabItem): TabKey =>
  item.kind === 'new' ? NEW_KEY : chartKey(item.symbol)

/** The tab a URL shows. */
export const keyFor = (symbol: string, view: ChartView | undefined): TabKey =>
  view === 'screener' ? SCREENER_KEY : view === 'new' ? NEW_KEY : chartKey(symbol)

/** The symbol a chart key names, or null for the screener and the New tab. */
export const symbolOf = (key: TabKey): string | null =>
  key.startsWith('chart:') ? key.slice('chart:'.length) : null

export const initialTabs = (): TabsState => ({ items: [], active: NEW_KEY, recent: [] })

const hasKey = (items: readonly TabItem[], key: TabKey): boolean =>
  items.some((item) => itemKey(item) === key)

/** `item` at the end; at the cap, the leftmost tab that is not `keep` makes room. */
const append = (items: readonly TabItem[], item: TabItem, keep: TabKey): TabItem[] => {
  if (items.length < MAX_TABS) return [...items, item]
  const drop = items.findIndex((kept) => itemKey(kept) !== keep)
  return [...items.slice(0, drop), ...items.slice(drop + 1), item]
}

const remember = (recent: readonly string[], symbol: string): string[] =>
  recent[0] === symbol
    ? [...recent]
    : [symbol, ...recent.filter((kept) => kept !== symbol)].slice(0, RECENT_LIMIT)

/**
 * The strip after the URL moved to `key`: a chart opened from anywhere (a link, the screener, a
 * cold `/chart/X`) gets a tab if it has none, and `?view=new` gets the New tab if there is none.
 * Returns `state` itself when nothing changed, so a caller can skip the write.
 */
export const showKey = (state: TabsState, key: TabKey): TabsState => {
  const symbol = symbolOf(key)
  const items =
    key === SCREENER_KEY || hasKey(state.items, key)
      ? state.items
      : append(state.items, symbol === null ? { kind: 'new' } : { kind: 'chart', symbol }, key)
  const recent =
    symbol === null || state.recent[0] === symbol ? state.recent : remember(state.recent, symbol)
  if (items === state.items && recent === state.recent && state.active === key) return state
  return { items, active: key, recent }
}

/**
 * The New tab turned into `symbol`'s chart tab, in its place (a pick on the New tab page). When the
 * symbol already has a tab, the New tab goes and that tab is the one shown.
 */
export const fillNewTab = (state: TabsState, symbol: string): TabsState => {
  const key = chartKey(symbol)
  const items = hasKey(state.items, key)
    ? state.items.filter((item) => item.kind !== 'new')
    : state.items.map((item): TabItem => (item.kind === 'new' ? { kind: 'chart', symbol } : item))
  return { ...state, items }
}

/**
 * The showing chart tab moved to `symbol` (a pick inside the chart, or in a chart panel), in its
 * place. When the symbol already has a tab of its own the strip is left alone, and showing that
 * symbol activates that tab instead.
 */
export const retarget = (state: TabsState, from: string, symbol: string): TabsState => {
  const key = chartKey(symbol)
  if (hasKey(state.items, key)) return state
  const fromKey = chartKey(from)
  return {
    ...state,
    items: state.items.map((item): TabItem =>
      itemKey(item) === fromKey ? { kind: 'chart', symbol } : item,
    ),
  }
}

/** The New tab at the end, unless there is one. */
export const addNewTab = (state: TabsState): TabsState =>
  hasKey(state.items, NEW_KEY)
    ? state
    : { ...state, items: append(state.items, { kind: 'new' }, NEW_KEY) }

export const canAddTab = (state: TabsState): boolean =>
  hasKey(state.items, NEW_KEY) || state.items.length < MAX_TABS

/**
 * `key` closed. Chrome's rule for what shows next when it was the one showing: its right
 * neighbour, else its left. Closing the last tab leaves a New tab, so the strip is never just the
 * screener. `next` is null when the closed tab was not showing (nothing moves).
 */
export const closeTab = (
  state: TabsState,
  key: TabKey,
  showing: TabKey,
): { state: TabsState; next: TabKey | null } => {
  const index = state.items.findIndex((item) => itemKey(item) === key)
  if (index < 0) return { state, next: null }
  const rest = state.items.filter((_, at) => at !== index)
  const items: TabItem[] = rest.length === 0 ? [{ kind: 'new' }] : rest
  const neighbour = items[Math.min(index, items.length - 1)]
  const next = key === showing && neighbour !== undefined ? itemKey(neighbour) : null
  return { state: { ...state, items }, next }
}

/** `key` moved to `index` among the items (drag, or Ctrl+Shift+arrow). */
export const moveTab = (state: TabsState, key: TabKey, index: number): TabsState => {
  const from = state.items.findIndex((item) => itemKey(item) === key)
  const to = Math.max(0, Math.min(index, state.items.length - 1))
  if (from < 0 || from === to) return state
  const items = [...state.items]
  const [moved] = items.splice(from, 1)
  if (moved === undefined) return state
  items.splice(to, 0, moved)
  return { ...state, items }
}
